<script>
/**
 * Componente reutilizable de EVIDENCIA FOTOGRÁFICA (ítems 1.1.6, 1.1.8, 4.1.2, 4.2.6).
 * Sube fotos por multipart al backend (que las redimensiona y guarda en el bucket) y las
 * lista como galería con miniaturas. Si se pasan reference-type/reference-id, las fotos
 * cuelgan de ese registro (entrega EPP / versión de matriz); si no, es galería general.
 *
 * Props:
 *  - company-id (req)      empresa
 *  - item-code (req)       '1.1.6' | '1.1.8' | '4.1.2' | '4.2.6'
 *  - reference-type/-id    (opcional) registro al que se asocian las fotos
 *  - category              'socializacion' | 'elemento_fisico' (etiqueta de propósito)
 *  - title / help-text     textos de la tarjeta
 *  - disabled              read-only (p.ej. documento ya firmado)
 * Emite 'changed' tras cada subida/borrado (para refrescar previsualizaciones del PDF).
 */
import photoEvidenceAPI from "@/services/standards/photoEvidence/photoEvidenceAPI";
import Swal from "sweetalert2";

export default {
  name: "PhotoEvidence",
  props: {
    companyId: { type: [Number, String], required: true },
    itemCode: { type: String, required: true },
    referenceType: { type: String, default: null },
    referenceId: { type: [Number, String], default: null },
    category: { type: String, default: null },
    title: { type: String, default: "Evidencia fotográfica" },
    helpText: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      photos: [],
      file: null,
      description: "",
      takenAt: "",
      uploading: false,
      loading: false,
    };
  },
  computed: {
    needsRecord() {
      return !!this.referenceType && !this.referenceId;
    },
    canUpload() {
      return !this.disabled && !this.needsRecord;
    },
  },
  watch: {
    referenceId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      if (this.needsRecord) {
        this.photos = [];
        return;
      }
      this.loading = true;
      try {
        const r = await photoEvidenceAPI.getAll({
          companyId: this.companyId,
          itemCode: this.itemCode,
          referenceType: this.referenceType,
          referenceId: this.referenceId,
        });
        this.photos = (r && r.data) ? r.data : [];
      } catch (e) {
        this.photos = [];
      } finally {
        this.loading = false;
      }
    },
    onFile(f) {
      this.file = f;
    },
    async upload() {
      if (!this.canUpload) return;
      if (!this.file) {
        Swal.fire("Atención", "Selecciona una imagen.", "info");
        return;
      }
      this.uploading = true;
      try {
        const res = await photoEvidenceAPI.save({
          companyId: this.companyId,
          itemCode: this.itemCode,
          referenceType: this.referenceType,
          referenceId: this.referenceId,
          category: this.category,
          description: this.description,
          takenAt: this.takenAt,
          file: this.file,
        });
        if (res && res.data) {
          this.photos.unshift(res.data);
          this.file = null;
          this.description = "";
          this.takenAt = "";
          this.$emit("changed");
        } else {
          Swal.fire("Error", (res && res.message) || "No se pudo cargar la imagen.", "error");
        }
      } catch (e) {
        Swal.fire("Error", "No se pudo cargar la imagen.", "error");
      } finally {
        this.uploading = false;
      }
    },
    fmtDate(v) {
      if (!v) return "";
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      return d.toLocaleDateString("es-CO", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    remove(photo, idx) {
      if (this.disabled) return;
      Swal.fire({
        title: "¿Eliminar esta foto?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then((r) => {
        if (!r.isConfirmed) return;
        photoEvidenceAPI.delete(photo.id).then(() => {
          this.photos.splice(idx, 1);
          this.$emit("changed");
        });
      });
    },
  },
};
</script>

<template>
  <div class="card">
    <div class="card-body">
      <h5 class="card-title mb-1">
        <i class="mdi mdi-camera-outline mr-1"></i>{{ title }}
      </h5>
      <p v-if="helpText" class="text-muted small mb-2">{{ helpText }}</p>

      <div v-if="needsRecord" class="alert alert-info py-2 small mb-2">
        Guarda el registro para habilitar el cargue de fotografías.
      </div>

      <!-- Formulario de subida -->
      <div v-if="canUpload" class="border rounded p-2 mb-3 bg-light">
        <div class="row">
          <div class="col-md-5 mb-2">
            <label class="small mb-1">Fotografía</label>
            <b-form-file
              v-model="file"
              accept="image/*"
              size="sm"
              placeholder="Selecciona una imagen..."
              browse-text="Buscar"
              @input="onFile"
            />
          </div>
          <div class="col-md-4 mb-2">
            <label class="small mb-1">Descripción (opcional)</label>
            <b-form-input v-model="description" size="sm" placeholder="Ej: Socialización COPASST" />
          </div>
          <div class="col-md-3 mb-2">
            <label class="small mb-1">Fecha (opcional)</label>
            <b-form-input v-model="takenAt" type="date" size="sm" />
          </div>
        </div>
        <button type="button" class="btn btn-primary btn-sm" :disabled="uploading || !file" @click="upload">
          <i class="mdi mdi-upload mr-1"></i>{{ uploading ? "Subiendo..." : "Subir foto" }}
        </button>
      </div>

      <!-- Galería -->
      <div v-if="loading" class="text-muted small">Cargando...</div>
      <div v-else-if="!photos.length" class="text-muted small">Sin fotografías cargadas.</div>
      <div v-else class="d-flex flex-wrap" style="gap:10px;">
        <div v-for="(p, idx) in photos" :key="p.id" class="border rounded" style="width:150px; overflow:hidden;">
          <a :href="p.image_url" target="_blank" rel="noopener">
            <img :src="p.image_url" alt="evidencia"
                 style="width:100%; height:110px; object-fit:cover; display:block; background:#eee;" />
          </a>
          <div class="p-1">
            <div class="small text-truncate" :title="p.description">{{ p.description || "—" }}</div>
            <div class="text-muted" style="font-size:10px;" v-if="p.taken_at">{{ fmtDate(p.taken_at) }}</div>
            <button v-if="!disabled" type="button" class="btn btn-outline-danger btn-sm btn-block py-0 mt-1"
                    style="font-size:11px;" @click="remove(p, idx)">
              <i class="mdi mdi-trash-can-outline"></i> Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
